// import axios from 'axios';

import { paths } from 'src/routes/paths';

import axios, { endpoints } from 'src/utils/axios';
import { voolistExtension } from 'src/utils/voolist-extension';
import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';

// ----------------------------------------------------------------------


async function connectEbay (marketplaceId, mode, integrationId) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'ebay', mode });
  const params = new URLSearchParams();
  if (mode) params.set('mode', mode);
  if (integrationId) params.set('integrationId', integrationId);
  const queryStr = params.toString() ? `?${params.toString()}` : '';
  const URL = `${endpoints.auth.ebay}/${marketplaceId}${queryStr}`
  const ebayUrl = (await axios.get(URL)).data?.url;
  window.location.href = ebayUrl;
}

async function connectShopify (shop, userId) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'shopify' });
  const authUrl = `${endpoints.auth.shopify}`;
  window.location.href = authUrl;
  // await axios.get(authUrl);
}

/**
 * Redeem the token Shopify handed back, which is what actually creates the connection.
 * @param {string} uuid the token from the `installed` param
 * @returns {Promise<{platform: string, integrationId: number|null}>}
 */
export async function linkShopify (uuid) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'shopify' });
  const URL = `${endpoints.auth.shopify}/link?uuid=${encodeURIComponent(uuid)}`;
  const response = await axios.get(URL);
  return response.data;
}


async function connectEtsy (mode, integrationId) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'etsy', mode });
  const params = new URLSearchParams();
  if (mode) params.set('mode', mode);
  if (integrationId) params.set('integrationId', integrationId);
  const queryStr = params.toString() ? `?${params.toString()}` : '';
  const URL = `${endpoints.auth.etsy}${queryStr}`;
  const etsyAuthUrl = (await axios.get(URL)).data?.url;
  window.location.href = etsyAuthUrl;
}

/**
 * Connect a WooCommerce store. 'oAuth' sends the seller to their store to approve Voolist; any other type
 * connects it with the API keys they typed in. Rejects with the reason, in words for the seller, when the
 * store can't be connected.
 */
async function connectWooCommerce (type, storeUrl, consumerKey, consumerSecret) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'wooCommerce', mode: type });
  const { data } = await axios.post(endpoints.auth.wooCommerce, { type, storeUrl, consumerKey, consumerSecret });

  if (type === 'oAuth') {
    window.location.href = data.url;
    return;
  }

  // Connected: come back the way every connect does, so one place decides what the seller is told and where
  // they go next, including back into setup.
  window.location = `${paths.management.stores}?${new URLSearchParams({
    connect: 'success', platform: 'wooCommerce', integrationId: String(data.integrationId),
  })}`;
}

const connectBigCommerce = async () => {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'bigCommerce' });
  const res = await axios.post(endpoints?.auth?.bigCommerce);
  window.location.href = res?.data?.appUrl;
}

/**
 * Which BigCommerce store an install is for, before the seller confirms it: its name when Voolist can read it,
 * and the BigCommerce account that added the app. `alreadyConnected` when this seller connected it already.
 */
export async function describeBigCommerceInstall (code) {
  const response = await axios.get(endpoints.auth.bigCommerceFinish, { params: { code } });
  return response.data;
}

/** Connect the BigCommerce store the seller confirmed. */
export async function finishBigCommerceInstall (code) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'bigCommerce', stage: 'finish' });
  const response = await axios.post(endpoints.auth.bigCommerceFinish, { code });
  return response.data;
}

// ----------------------------------------------------------------------
// Extension-based marketplace connections
// These platforms don't have official APIs - connection is handled by the Chrome extension
// The connect action redirects users to the platform's website
// ----------------------------------------------------------------------

// Extension platform domain mapping
const EXTENSION_PLATFORM_URLS = {
  // Vinted regional domains
  vintedFr: 'https://www.vinted.fr',
  vintedDe: 'https://www.vinted.de',
  vintedUs: 'https://www.vinted.com',
  vintedUk: 'https://www.vinted.co.uk',
  vintedEs: 'https://www.vinted.es',
  vintedIt: 'https://www.vinted.it',
  vintedPl: 'https://www.vinted.pl',
  vintedNl: 'https://www.vinted.nl',
  vintedBe: 'https://www.vinted.be',
  vintedAt: 'https://www.vinted.at',
  vintedCz: 'https://www.vinted.cz',
  vintedLt: 'https://www.vinted.lt',
  vintedPt: 'https://www.vinted.pt',
  // Poshmark
  poshmarkUs: 'https://poshmark.com',
  poshmarkCa: 'https://poshmark.ca',
  // Mercari
  mercariUs: 'https://www.mercari.com',
  // Depop
  depop: 'https://www.depop.com',
};

/**
 * Connect extension-based platform by redirecting to the platform's website
 * The Chrome extension will handle the actual connection when the user logs in
 * @param {string} marketplaceName - The marketplace name (e.g., 'vintedDe', 'poshmarkUs')
 */
function connectExtensionPlatform (marketplaceName) {
  const url = EXTENSION_PLATFORM_URLS[marketplaceName];
  if (url) {
    window.open(url, '_blank');
  } else {
    console.error(`No URL configured for extension platform: ${marketplaceName}`);
  }
}

// Individual connect functions for each extension platform
function connectVinted (marketplaceName) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'vinted' });
  connectExtensionPlatform(marketplaceName);
}

async function connectPoshmark (marketplaceName) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'poshmark' });
  try {
    const result = await voolistExtension.connectPlatform('poshmark', marketplaceName);
    return result;
  } catch {
    // Extension not installed or timeout — fall back to opening the marketplace
    connectExtensionPlatform(marketplaceName);
    return undefined;
  }
}

function connectMercari (marketplaceName) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'mercari' });
  connectExtensionPlatform(marketplaceName);
}

async function connectDepop () {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform: 'depop' });
  try {
    const result = await voolistExtension.connectPlatform('depop', 'depop');
    return result;
  } catch {
    // Extension not installed or timeout — fall back to opening the marketplace
    connectExtensionPlatform('depop');
    return undefined;
  }
}

// A connect of a marketplace worked on through its own pages may first open a page of it and wait for
// it to load before it reads who is signed in there, so it is given longer than the others to answer.
const OWN_PAGES_CONNECT_TIMEOUT = 45000;

/**
 * The connect of a marketplace Voolist works on only through the seller's own pages of it — Grailed, Whatnot,
 * OfferUp and Vestiaire. The connect can wait on the seller twice — to allow Voolist on the marketplace's site, then to
 * sign in there — and answers `pending` with what it waits for; the extension pushes the next step, the
 * connection or the reason it stopped.
 *
 * There is no page of the marketplace to fall back to: without the extension it cannot be connected, so
 * an extension that does not answer leaves the caller to say so.
 *
 * @param {'grailed'|'whatnot'|'offerup'|'vestiaire'} platform
 * @returns {function(): Promise<(Object|undefined)>} The connect: the extension's answer, or undefined
 *   when none came
 */
const connectThroughOwnPages = (platform) => async () => {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_CONNECT_STARTED, { platform });
  try {
    return await voolistExtension.connectPlatform(platform, platform, { timeoutMs: OWN_PAGES_CONNECT_TIMEOUT });
  } catch {
    return undefined;
  }
};

async function revokeIntegration (name) {
  const URL = endpoints.auth.revoke;

  try {
    await axios.delete(URL, { data: { name } });
    window.location.reload();
  } catch (error) {
    // Still reload even on error to show error state
    window.location.reload();
  }
}

/**
 * Disconnect a specific account by integration ID (multi-account support).
 * @param {number} integrationId - The integration ID to disconnect
 * @param {string} [platform] - Marketplace name, used for analytics only (low-cardinality, non-PII)
 */
export async function disconnectAccount (integrationId, platform) {
  trackEvent(ANALYTICS_EVENTS.PLATFORM_DISCONNECT_CLICKED, { platform });
  const URL = `${endpoints.auth.integrations}/${integrationId}`;
  try {
    const response = await axios.delete(URL);
    trackEvent(ANALYTICS_EVENTS.PLATFORM_DISCONNECT_SUCCEEDED, { platform });
    window.location.reload();
    return response.data;
  } catch (error) {
    window.location.reload();
    throw new Error(error.response?.data?.message || 'Failed to disconnect account');
  }
}

/**
 * Update the label for a specific account (multi-account support).
 * @param {number} integrationId - The integration ID
 * @param {string} label - The new display label
 * @returns {Promise<Object>} The updated integration data
 */
export async function updateAccountLabel (integrationId, label) {
  const URL = `${endpoints.auth.integrations}/${integrationId}/label`;
  try {
    const response = await axios.patch(URL, { label });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Couldn't rename the account. Try again.");
  }
}

/**
 * Connect a new account for a platform (multi-account).
 * Passes mode=connect_new to the OAuth URL to signal new account creation.
 */
export function connectNewEbayAccount (marketplaceId) {
  return connectEbay(marketplaceId, 'connect_new');
}

export function connectNewEtsyAccount () {
  return connectEtsy('connect_new');
}

/**
 * Update the auto-delist setting for an integration
 * @param {number} integrationId - The integration ID
 * @param {boolean} enabled - Whether auto-delist should be enabled
 * @returns {Promise<Object>} The updated integration data
 */
export async function updateAutoDelistSetting (integrationId, enabled) {
  const URL = `${endpoints.auth.integrations}/${integrationId}/auto-delist`;

  try {
    const response = await axios.patch(URL, { enabled });
    return response.data;
  } catch (error) {
    console.error('Error updating auto-delist setting:', error);
    throw new Error(error.response?.data?.message || "Couldn't change Sales Detection. Try again.");
  }
}


/**
 * Update the auto-offer settings for an integration
 * @param {number} integrationId - The integration ID
 * @param {Object} settings - { enabled, discountPercentage, message, offerDuration }
 * @returns {Promise<Object>} The updated integration data
 */
export async function updateAutoOfferSettings (integrationId, settings) {
  const URL = `${endpoints.auth.integrations}/${integrationId}/auto-offer`;

  try {
    const response = await axios.patch(URL, settings);
    return response.data;
  } catch (error) {
    console.error('Error updating auto-offer settings:', error);
    throw new Error(error.response?.data?.message || "Couldn't save Auto Offer to Watchers. Try again.");
  }
}

/**
 * Save how a Depop account ships.
 *
 * Depop prices its own postage labels for the accounts that have them, and every other account
 * ships on a flat price the seller sets. Depop publishes nothing we can read to tell the two apart,
 * so the seller's choice is kept on the account here.
 *
 * @param {number} integrationId - The integration ID
 * @param {Object} settings - { mode: 'labels'|'ownCost'|'auto', nationalShippingCost }
 * @returns {Promise<Object>} The updated integration data
 */
export async function updateDepopShippingSettings (integrationId, settings) {
  const URL = `${endpoints.auth.integrations}/${integrationId}/depop-shipping`;

  try {
    const response = await axios.patch(URL, settings);
    return response.data;
  } catch (error) {
    console.error('Error updating Depop shipping settings:', error);
    throw new Error(error.response?.data?.message || "Couldn't save your Depop shipping choice. Try again.");
  }
}

const marketplaceConnection = {
  ebay: connectEbay,
  etsy: connectEtsy,
  shopify: connectShopify,
  wooCommerce: connectWooCommerce,
  bigCommerce: connectBigCommerce,
  // Extension-based platforms
  vinted: connectVinted,
  poshmark: connectPoshmark,
  mercari: connectMercari,
  depop: connectDepop,
  grailed: connectThroughOwnPages('grailed'),
  whatnot: connectThroughOwnPages('whatnot'),
  offerup: connectThroughOwnPages('offerup'),
  vestiaire: connectThroughOwnPages('vestiaire'),
  revokeIntegration,
};

export default marketplaceConnection;